'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface PreviewFrameReadyValue {
  frameEverReady: boolean;
  markFrameReady: () => void;
}

const PreviewFrameReadyContext = createContext<PreviewFrameReadyValue>({
  frameEverReady: false,
  markFrameReady: () => {},
});

/**
 * Tracks whether the preview frame has reported ready at least once.
 *
 * The target site mints the visitor session when it has none, so anything that
 * depends on that identity -- the metasite instance token, and through it the
 * session and comments requests -- has to wait for the frame instead of racing
 * it and minting a visitor id the site then replaces.
 *
 * The flag is latched: a later frame reload (device switch, navigation) must not
 * re-block the requests or flicker the guest modal away.
 */
export function PreviewFrameReadyProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [frameEverReady, setFrameEverReady] = useState(false);
  const markFrameReady = useCallback(() => setFrameEverReady(true), []);
  const value = useMemo(
    () => ({ frameEverReady, markFrameReady }),
    [frameEverReady, markFrameReady],
  );

  return (
    <PreviewFrameReadyContext.Provider value={value}>
      {children}
    </PreviewFrameReadyContext.Provider>
  );
}

export function usePreviewFrameReady() {
  return useContext(PreviewFrameReadyContext);
}
